import { cva, type VariantProps } from 'class-variance-authority';
import {
  disabledState,
  focusRing,
  focusRingOffset,
  transitionBase,
  uiMarker,
  uiMarkerLayout,
} from './shared';

/**
 * CHOICE CONTROLS — checkbox, switch and radio.
 *
 * All three answer a question with a small fixed set of answers, but they are
 * not interchangeable and the recipes are kept apart to make that obvious:
 *
 *   • Checkbox — the value is submitted with a form. Nothing happens until the
 *     user saves. Also the only one of the three that can be indeterminate.
 *   • Switch — takes effect immediately, like a light switch. If it needs a
 *     Save button next to it, it should have been a checkbox.
 *   • Radio — exactly one of several. Round, where a checkbox is square: that
 *     shape difference is the only thing telling a user they cannot pick two,
 *     so do not round the checkbox or square the radio.
 */

export const checkboxRecipe = cva(
  [
    uiMarker,
    'peer shrink-0 rounded-sm border border-input bg-background',
    'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
    'data-[state=checked]:text-primary-foreground',
    'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary',
    'data-[state=indeterminate]:text-primary-foreground',
    'aria-[invalid=true]:border-destructive',
    transitionBase,
    focusRingOffset,
    disabledState,
  ],
  {
    variants: {
      size: {
        sm: 'size-4',
        md: 'size-5',
        /**
         * The phone's thumb-sized box, at the end of a row in a sheet — the
         * Operators sheet's 32px square with 4px corners. Still a square,
         * so it still says "pick any number".
         */
        lg: 'size-8 rounded border-foreground/10',
      },
    },
    defaultVariants: { size: 'sm' },
  }
);

export type CheckboxRecipeProps = VariantProps<typeof checkboxRecipe>;

/** The tick/dash mark inside the box, sized to it. */
export const checkboxIndicatorRecipe = cva(
  [uiMarker, 'flex items-center justify-center text-current'],
  {
    variants: {
      size: {
        sm: '[&_svg]:size-3',
        md: '[&_svg]:size-3',
        lg: '[&_svg]:size-5',
      },
    },
    defaultVariants: { size: 'sm' },
  }
);

export const switchRecipe = cva(
  [
    uiMarker,
    'peer inline-flex shrink-0 cursor-pointer items-center rounded-pill border-2 border-transparent',
    'data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
    transitionBase,
    focusRingOffset,
    disabledState,
  ],
  {
    variants: {
      size: {
        sm: 'h-5 w-9',
        md: 'h-6 w-11',
      },
    },
    defaultVariants: { size: 'sm' },
  }
);

/** The sliding knob. Travel distance has to match the track width above. */
export const switchThumbRecipe = cva(
  [
    uiMarker,
    'pointer-events-none block rounded-pill bg-background shadow-e1 ring-0',
    'transition-transform duration-fast',
    'data-[state=unchecked]:translate-x-0',
  ],
  {
    variants: {
      size: {
        sm: 'size-4 data-[state=checked]:translate-x-4',
        md: 'size-5 data-[state=checked]:translate-x-5',
      },
    },
    defaultVariants: { size: 'sm' },
  }
);

/**
 * Wrapper for a control with its label beside it, rather than above.
 *
 * `items-start` rather than centre: with a two-line label a centred checkbox
 * floats away from the first line, which reads as belonging to neither.
 */
/*
 * Radio. Deliberately mirrors the checkbox — same size scale, same border and
 * focus treatment — so a form mixing the two looks like one form. The only
 * intentional differences are the round shape and the filled dot instead of a
 * tick, which are what tell the user this is a pick-one.
 */
export const radioRecipe = cva(
  [
    uiMarker,
    'peer aspect-square shrink-0 rounded-pill border border-input bg-background',
    'data-[state=checked]:border-primary',
    'aria-[invalid=true]:border-destructive',
    transitionBase,
    focusRingOffset,
    disabledState,
  ],
  {
    variants: {
      size: {
        sm: 'size-4',
        md: 'size-5',
      },
    },
    defaultVariants: { size: 'sm' },
  }
);

export type RadioRecipeProps = VariantProps<typeof radioRecipe>;

/** The filled dot inside a selected radio. */
export const radioIndicatorRecipe = cva(
  [
    uiMarker,
    'flex size-full items-center justify-center after:block after:rounded-pill after:bg-primary',
  ],
  {
    variants: {
      size: {
        sm: 'after:size-1.5',
        md: 'after:size-2',
      },
    },
    defaultVariants: { size: 'sm' },
  }
);

/** Spacing between the options in a group. */
export const radioGroupRecipe = cva([uiMarker, 'grid'], {
  variants: {
    orientation: {
      vertical: 'gap-2',
      /** Only for two or three short options — it wraps badly otherwise. */
      horizontal: 'grid-flow-col auto-cols-max gap-4',
    },
  },
  defaultVariants: { orientation: 'vertical' },
});

export type RadioGroupRecipeProps = VariantProps<typeof radioGroupRecipe>;

export const controlRowRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex items-start gap-2.5'],
  {
    variants: {
      /** Puts the control after the label — for switches in settings rows. */
      reverse: {
        true: 'flex-row-reverse justify-between',
        false: '',
      },
    },
    defaultVariants: { reverse: false },
  }
);

/**
 * SEGMENTED CONTROL
 *
 * The selected item is filled rather than merely tinted: at icon size a tint
 * is not a strong enough signal for which view you are looking at.
 */
export const toggleGroupRecipe = cva([uiMarker, 'inline-flex items-center'], {
  variants: {
    bordered: {
      true: 'rounded-md border border-border p-1',
      false: '',
    },
    /**
     * The room between items. `2` for a row of bordered options, `0` for a
     * segmented track, whose options meet.
     */
    gap: { 0: 'gap-0', 1: 'gap-1', 2: 'gap-2' },
    /** Let a long row of options run onto a second line. */
    wrap: { true: 'flex-wrap', false: '' },
    /**
     * Fill the row and share it equally: a question with two answers side
     * by side, Pass and Fail, Yes and No.
     */
    fullWidth: { true: 'flex w-full [&>*]:flex-1', false: '' },
    /**
     * `pill` is the studio's floating device switch: fully rounded ends on
     * the group and on each item, with a tighter inset so the on-state
     * fills the group rather than sitting in a box inside it.
     */
    shape: {
      rounded: '',
      pill: 'rounded-pill bg-background p-0.5',
      /**
       * The phone's segmented control (Figma 577:140196's Activity Sort):
       * the whole width, a 5% ink track 2px round its options, fully
       * rounded. Pair with `variant="segment"` and `size="segment"`.
       */
      segmented: 'flex w-full rounded-pill bg-foreground/5 p-0.5',
    },
  },
  defaultVariants: {
    bordered: true,
    shape: 'rounded',
    gap: 1,
    wrap: false,
    fullWidth: false,
  },
});
export type ToggleGroupRecipeProps = VariantProps<typeof toggleGroupRecipe>;

export const toggleGroupItemRecipe = cva(
  [
    uiMarker,
    'inline-flex items-center justify-center gap-1.5 font-medium',
    transitionBase,
    focusRing,
    disabledState,
  ],
  {
    variants: {
      /**
       * How loudly the selected item is marked.
       *
       * `solid` fills the selected one, which is right for an icon-only switch
       * where a tint would not carry. `plain` is for a toolbar that sits above
       * a list: the two words are the label of what you are looking at, not an
       * action, and a green button there reads as something to press. `chip`
       * fills both, so a question with two written answers reads as two things
       * you can press rather than one button and some loose words.
       *
       * Every variant names its own text colour. Leaving the unselected item
       * to inherit is how it went white on white in the driver app, which sets
       * `color-scheme: light dark` and has no dark theme: the browser's own
       * text colour flips with the OS while the background stays white.
       */
      variant: {
        solid: [
          'rounded-sm text-foreground hover:bg-muted hover:text-foreground',
          'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
          'data-[state=on]:hover:bg-primary/90',
        ],
        plain: [
          'rounded-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground',
          'data-[state=on]:text-foreground data-[state=on]:font-semibold',
        ],
        chip: [
          'rounded-lg bg-muted text-foreground hover:bg-muted/70',
          'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
          'data-[state=on]:hover:bg-primary/90',
        ],
        /**
         * An option drawn in an Input's chrome, with the primary rule when
         * on. A question answered by pressing one of a few written choices
         * — "A truck", "Something else" — where three words beside a 16px
         * radio read as a form and the circle is the smallest target on the
         * page. `shape="pill"` for one answer, the square for any number.
         */
        outline: [
          'rounded-md border border-input bg-background text-foreground',
          'data-[state=on]:border-primary',
        ],
        /**
         * The List / Table switch in the dashboard's framed toolbar (Figma
         * 25:3423): each word in its own hairline box, the current one
         * washed and full black, the other half black.
         */
        framed: [
          'rounded-lg border border-foreground/10 text-muted-foreground hover:text-foreground',
          'data-[state=on]:bg-foreground/5 data-[state=on]:text-foreground',
        ],
        /**
         * One option of the phone's segmented control: the current one a
         * white pill in ink, the others half black on the track itself.
         */
        segment: [
          'text-muted-foreground hover:text-foreground',
          'data-[state=on]:bg-card data-[state=on]:text-foreground',
        ],
      },
      size: {
        sm: 'h-7 min-w-7 px-2 text-caption',
        /**
         * `sm`'s height with a 20px glyph and a 14px word, packed to a 4px
         * inset: the List / Table switch over a list, which has to fit
         * beside two toolbar words and a magnifier in a 400 column.
         */
        compact:
          'h-7 min-w-7 gap-1 px-1 text-label [&_svg]:size-icon-md [&_svg]:shrink-0',
        /** 30 high, a 20px glyph and a 14px word 8px in: the framed toolbar's switch. */
        toolbar:
          'h-[30px] gap-1 px-2 text-label [&_svg]:size-icon-md [&_svg]:shrink-0',
        /** An Input's height, so a row of these lines up with a field beside it in a grid. */
        field: 'h-control-sm px-3 text-body-sm',
        md: 'h-9 min-w-9 px-2.5 text-body-sm',
        lg: 'h-10 min-w-10 px-3 text-body [&_svg]:size-icon-md',
        /** 36 high in the 40 track, at the phone's 18px row-title weight. */
        segment: 'h-9 px-3 text-body-lg-strong',
      },
      shape: {
        rounded: '',
        pill: 'rounded-pill',
        /** An equal share of the segmented track, fully rounded. */
        segmented: 'flex-1 rounded-pill',
      },
      /**
       * The meaning of an answer, for an `outline` option whose answer is a
       * verdict: Pass takes `success` and Fail `destructive`. Chosen, it keeps
       * the outline's shape and takes the subtle fill of its meaning, so the
       * pair reads as one control and the verdict reads before the word.
       */
      tone: { none: '', success: '', destructive: '' },
    },
    compoundVariants: [
      {
        variant: 'outline',
        tone: 'success',
        class:
          'shadow-e1 data-[state=on]:border-success data-[state=on]:bg-success-subtle data-[state=on]:text-success-subtle-foreground',
      },
      {
        variant: 'outline',
        tone: 'destructive',
        class:
          'shadow-e1 data-[state=on]:border-destructive data-[state=on]:bg-destructive-subtle data-[state=on]:text-destructive-subtle-foreground',
      },
    ],
    defaultVariants: {
      variant: 'solid',
      size: 'md',
      shape: 'rounded',
      tone: 'none',
    },
  }
);
export type ToggleGroupItemRecipeProps = VariantProps<
  typeof toggleGroupItemRecipe
>;
